import { useRef, useEffect, useCallback, useMemo } from "react"
import type { MapRef } from "react-map-gl/mapbox"
import distance from "@turf/distance"
import { point } from "@turf/helpers"
import { debounce } from "lodash"
import { getCommuneFromCoordinates } from "@/lib/explore/api/geo"
import type { ICommune } from "@/types/explore"

interface UseAutoCommuneProps {
  mapRef: React.RefObject<MapRef | null>
  selectedCommune: ICommune | undefined
  onSelectCommune: (code: string) => Promise<void>
  setSelectedCommune: (commune: ICommune) => void
}

export const useAutoCommune = ({ mapRef, selectedCommune, onSelectCommune }: UseAutoCommuneProps) => {
  const wasAutoSelectedRef = useRef(false)
  const isLoadingCommuneRef = useRef(false)
  const lastCommuneCenterRef = useRef<{ lat: number; lon: number } | null>(null)
  const selectedCommuneCodeRef = useRef<string | undefined>(undefined)
  const onSelectCommuneRef = useRef(onSelectCommune)

  // Keep refs in sync
  useEffect(() => {
    selectedCommuneCodeRef.current = selectedCommune?.code
  }, [selectedCommune?.code])
  useEffect(() => {
    onSelectCommuneRef.current = onSelectCommune
  }, [onSelectCommune])

  const handleMapMoveEnd = useCallback(async () => {
    const map = mapRef.current?.getMap()
    if (!map || isLoadingCommuneRef.current) return

    const zoom = map.getZoom()
    if (zoom <= 12) return

    const center = map.getCenter()
    const lat = center.lat
    const lon = center.lng

    if (lastCommuneCenterRef.current) {
      const dist = distance(
        point([lon, lat]),
        point([lastCommuneCenterRef.current.lon, lastCommuneCenterRef.current.lat]),
        { units: "kilometers" },
      )
      if (dist < 1) return
    }

    isLoadingCommuneRef.current = true
    try {
      const result = await getCommuneFromCoordinates(lat, lon)
      if (result && result.code !== selectedCommuneCodeRef.current) {
        lastCommuneCenterRef.current = { lat, lon }
        wasAutoSelectedRef.current = true
        await onSelectCommuneRef.current(result.code)
      }
    } catch (error) {
      console.error("Erreur lors de la detection de la commune:", error)
    } finally {
      isLoadingCommuneRef.current = false
    }
  }, [])

  const debouncedHandleMapMoveEnd = useMemo(() => debounce(handleMapMoveEnd, 300), [handleMapMoveEnd])

  useEffect(() => {
    return () => {
      debouncedHandleMapMoveEnd.cancel()
    }
  }, [debouncedHandleMapMoveEnd])

  const handleManualSelectCommune = useCallback(
    async (code: string) => {
      return onSelectCommune(code)
    },
    [onSelectCommune],
  )

  return {
    wasAutoSelectedRef,
    debouncedHandleMapMoveEnd,
    handleManualSelectCommune,
  }
}
